/**
 * =============================================================================
 * Componente: AffiliateAvatarUploader
 * Versão: 1.0.0
 *
 * Descrição:
 *   Card de upload/remoção da foto de perfil do afiliado.
 *   - Bucket público: `affiliate`
 *   - Caminho: `avatars/{affiliate_id}/avatar.{ext}` (upsert, sobrescreve)
 *   - Persistência: salva a URL pública completa em `affiliates.avatar_url`
 *     via RPC `update_affiliate_profile_safe` (envia null para remover).
 *   - Validações: tipos JPG/PNG/WebP, máximo 5 MB.
 * =============================================================================
 */
import { useRef, useState, useEffect } from 'react';
import { Camera, Loader2, Trash2, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { supabase } from '@/integrations/supabase/client';
import { useUpdateAffiliateProfile } from '@/hooks/useAffiliateProfile';
import { createLogger } from '@/lib/logger';

const log = createLogger('AffiliateAvatarUploader');

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const BUCKET = 'affiliate';

interface AffiliateAvatarUploaderProps {
  /** UUID do afiliado (necessário para compor o caminho no storage). */
  affiliateId: string;
  /** Nome do afiliado (fallback de iniciais coloridas). */
  affiliateName?: string | null;
  /** URL pública atual do avatar (ou null/undefined se não houver). */
  currentAvatarUrl: string | null | undefined;
}

/** Extrai a extensão do arquivo (jpg por padrão). */
function getExtension(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase();
  if (fromName && fromName.length <= 5) return fromName;
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/webp') return 'webp';
  return 'jpg';
}

export function AffiliateAvatarUploader({
  affiliateId,
  affiliateName,
  currentAvatarUrl,
}: AffiliateAvatarUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const updateProfile = useUpdateAffiliateProfile();

  // Limpa preview ao desmontar para liberar memória.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const displayUrl = previewUrl ?? currentAvatarUrl ?? null;

  /** Upload completo: storage + persiste URL no perfil. */
  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validação de tipo
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Formato inválido', {
        description: 'Use JPG, PNG ou WebP.',
      });
      return;
    }

    // Validação de tamanho
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Arquivo muito grande', {
        description: 'Tamanho máximo: 5 MB.',
      });
      return;
    }

    // Preview imediato
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);

    setBusy(true);
    try {
      const ext = getExtension(file);
      const path = `avatars/${affiliateId}/avatar.${ext}`;

      log.info('Upload de avatar iniciado', { path, size: file.size });

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, {
          upsert: true,
          contentType: file.type,
          cacheControl: '3600',
        });

      if (uploadError) {
        log.error('Erro no upload', uploadError);
        throw new Error('Falha ao enviar a imagem.');
      }

      // Gera URL pública. Adiciona timestamp para invalidar cache do navegador
      // após sobrescrever o mesmo path.
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
      const publicUrl = `${pub.publicUrl}?v=${Date.now()}`;

      await updateProfile.mutateAsync({ avatar_url: publicUrl });

      toast.success('Foto atualizada com sucesso.');
    } catch (err: any) {
      log.error('Falha ao salvar avatar', err);
      toast.error(err?.message ?? 'Erro ao salvar foto.');
      // Reverte preview em caso de falha
      setPreviewUrl(null);
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  /** Remove a foto: limpa storage (best-effort) e zera coluna no banco. */
  async function handleRemove() {
    if (!currentAvatarUrl) return;

    setBusy(true);
    try {
      // Tenta apagar todos os possíveis arquivos da pasta do afiliado.
      // Como a extensão pode variar, listamos e deletamos tudo dentro.
      const { data: list } = await supabase.storage
        .from(BUCKET)
        .list(`avatars/${affiliateId}`);

      if (list && list.length > 0) {
        const paths = list.map((f) => `avatars/${affiliateId}/${f.name}`);
        await supabase.storage.from(BUCKET).remove(paths);
      }

      // null sinaliza limpeza para o hook → string vazia para a RPC
      await updateProfile.mutateAsync({ avatar_url: null });

      setPreviewUrl(null);
      toast.success('Foto removida.');
    } catch (err: any) {
      log.error('Falha ao remover avatar', err);
      toast.error(err?.message ?? 'Erro ao remover foto.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="aff-card overflow-hidden">
      <header className="flex items-center gap-3 border-b border-[hsl(var(--aff-border))] px-5 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--aff-primary-soft))] text-[hsl(var(--aff-primary))]">
          <ImageIcon className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-[hsl(var(--aff-foreground))]">
            Foto de perfil
          </h2>
          <p className="text-[11px] text-[hsl(var(--aff-muted-foreground))]">
            Aparece no painel e no formulário público de indicação
          </p>
        </div>
      </header>

      <div className="p-5 flex flex-col items-center gap-4 sm:flex-row sm:items-center">
        <div className="relative">
          <SmartAvatar
            src={displayUrl}
            name={affiliateName}
            size="2xl"
            className="border-2 border-[hsl(var(--aff-border))]"
          />
          {busy && (
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
              <Loader2 className="h-6 w-6 animate-spin text-white" />
            </div>
          )}
        </div>

        <div className="flex-1 space-y-2 text-center sm:text-left">
          <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
              className="gap-2"
            >
              <Camera className="h-4 w-4" />
              {currentAvatarUrl ? 'Alterar foto' : 'Enviar foto'}
            </Button>
            {currentAvatarUrl && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={handleRemove}
                className="gap-2 text-[hsl(var(--aff-danger))] hover:text-[hsl(var(--aff-danger))]"
              >
                <Trash2 className="h-4 w-4" />
                Remover
              </Button>
            )}
          </div>
          <p className="text-[11px] text-[hsl(var(--aff-muted-foreground))]">
            JPG, PNG ou WebP — até 5 MB.
          </p>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    </section>
  );
}
